import { __spreadArray } from "tslib";
import { Contracts, makeInt64DataField, makeStringDataField, Session, SuppressNexus, TelemetryLogger, User, } from '@microsoft/oteljs';
import { OneDSEndpoint, OneDSSink } from '@microsoft/oteljs-1ds';
import { HubSdkVersion } from './constants';
import { DataResidency } from './hosted-web-content-sdk.interface';
import { HostClientType } from './messages.interface';
import { checkIfTestRun } from './utils';
import { isFullAppContext, sanitizeMessage } from './utils/helpers';
var getCurrentMicroseconds = function () { return Date.now() * 1000; };
if (typeof window === 'object' && typeof window.performance === 'object' && 'now' in window.performance) {
    getCurrentMicroseconds = function () { return Math.floor(window.performance.now()) * 1000; };
}
export var adjustLastMark = function (clientTimestamp) {
    // timestamp provided by the caller is a UNIX timestamp
    // but internally we're using performance markers (if supported by the browser)
    // so we need to do some adjusting
    var now = Date.now();
    var nowMicroseconds = getCurrentMicroseconds();
    return nowMicroseconds - (now - clientTimestamp) * 1000;
};
/**
 * The telemetry service for Hub SDK
 */
var HubSdkTelemetryService = /** @class */ (function () {
    /**
     * Create a wrapper telemetry service on top of the custom telemetry service
     * @param hubInfo information about current hub
     * @param appContext app context info for current app session
     * @param customTelemetryService custom telemetry service to proxy events to
     */
    function HubSdkTelemetryService(hubInfo, appContext, customTelemetryService) {
        this.hubInfo = hubInfo;
        this.customTelemetryService = customTelemetryService;
        this.letterRegex = /[a-zA-Z]/;
        this.consumerTid = '9188040d-6c67-4c5b-b112-36a304b66dad';
        this.telemetrySource = 'AppHostSdk';
        this.namespace = 'Office.AppHostingSdk';
        this.eventFieldPrefix = 'AppHost';
        this.ariaTenantToken = '7ada8d16f9234e2093f080920f21dd7d-41cee27e-93c2-4a6b-a7aa-2001e852d387-7123';
        this.nexusTenantToken = SuppressNexus; // Nexus is not needed as it will be deprecated
        this.anonymousDataFields = __spreadArray([], Contracts.Office.System.User.getFields('User', {
            isAnonymous: true,
        }), true);
        // Create static data fields for sink
        var dataFields = [
            makeStringDataField("".concat(this.eventFieldPrefix, ".HostName"), this.hubInfo.name),
            makeStringDataField("".concat(this.eventFieldPrefix, ".HostVersion"), this.hubInfo.version),
            makeStringDataField("".concat(this.eventFieldPrefix, ".HostSdkVersion"), HubSdkVersion),
            makeStringDataField("".concat(this.eventFieldPrefix, ".HostSessionId"), this.hubInfo.sessionId),
            makeStringDataField("".concat(this.eventFieldPrefix, ".TelemetrySource"), this.telemetrySource),
            makeStringDataField("".concat(this.eventFieldPrefix, ".Platform"), appContext.app.host.clientType === HostClientType.web ? 'Web' : 'Desktop'),
        ];
        // create logger
        this.telemetryLogger = new TelemetryLogger();
        this.telemetryLogger.setTenantToken(this.namespace, this.ariaTenantToken, this.nexusTenantToken);
        var sinkEndpoint = getOTelSinkEndpoint(appContext.user.dataResidency);
        if (sinkEndpoint) {
            this.oneDsSink = new OneDSSink(dataFields, { endpointUrl: sinkEndpoint });
            this.telemetryLogger.telemetrySinks = [this.oneDsSink];
        }
        this.setAppContext(appContext);
        this.isTest = checkIfTestRun();
        this.enableExtendedInitializationTracking = (customTelemetryService === null || customTelemetryService === void 0 ? void 0 : customTelemetryService.enableExtendedInitializationTracking) || false;
    }
    /**
     * Initializing a telemetry scenario based on the named API
     * @param name name of API scenario that is being run
     * @param appId appId of the hosted application
     * @param clientTimestamp timestamp for when the message was created by the App SDK
     */
    HubSdkTelemetryService.prototype.createScenario = function (name, appId, clientTimestamp) {
        var _a, _b;
        if (clientTimestamp === void 0) { clientTimestamp = Date.now(); }
        appId = appId !== null && appId !== void 0 ? appId : (_a = this.appDefinition) === null || _a === void 0 ? void 0 : _a.id;
        var activity = this.isTest
            ? undefined
            : this.telemetryLogger.createActivity(this.createTelemetryEvent(name, clientTimestamp, appId));
        var customScenario = (_b = this.customTelemetryService) === null || _b === void 0 ? void 0 : _b.createScenario(name, appId, clientTimestamp);
        var shouldLogToAria = this.shouldLogToAria(name);
        return new HubSdkTelemetryScenario(this, shouldLogToAria, clientTimestamp, customScenario, activity);
    };
    /**
     * Set the App SDK version sent as the telemetry metadata
     * @param version App SDK version used by the hosted application
     */
    HubSdkTelemetryService.prototype.setAppSdkVersion = function (version) {
        this.appSdkVersion = version;
    };
    /**
     * Set the app context sent as the telemetry metadata
     * @param appContext the app definition
     */
    HubSdkTelemetryService.prototype.setAppContext = function (appContext) {
        var _a, _b, _c, _d, _e, _f, _g;
        var prevAppContext = this.appContext;
        this.appContext = appContext;
        if (!this.oneDsSink) {
            return;
        }
        // NOTE: OTel suggested that we keep appending to existing OneDSSink for perf. Last-in wins.
        // Add user metadata
        if (isFullAppContext(appContext)) {
            if (!isFullAppContext(prevAppContext) || appContext.user.id !== prevAppContext.user.id) {
                var isAnonymous = !appContext.user.id;
                var userDataFields = void 0;
                if (isAnonymous) {
                    userDataFields = this.anonymousDataFields;
                }
                else {
                    var isCommercial = ((_b = (_a = this.appContext.user) === null || _a === void 0 ? void 0 : _a.tenant) === null || _b === void 0 ? void 0 : _b.id) !== this.consumerTid;
                    userDataFields = User.getFields({
                        primaryIdentityHash: (_c = appContext.user) === null || _c === void 0 ? void 0 : _c.id,
                        primaryIdentitySpace: isCommercial ? 'UserObjectId' : 'MSAPuid',
                        tenantId: (_e = (_d = this.appContext.user) === null || _d === void 0 ? void 0 : _d.tenant) === null || _e === void 0 ? void 0 : _e.id,
                        tenantGroup: isCommercial ? 'Commercial' : 'Consumer',
                        isAnonymous: isAnonymous,
                    });
                }
                // add these to the sink directly to avoid the column being prepended with Data.
                this.oneDsSink.addPersistentDataFields(userDataFields);
            }
        }
        // Add session id
        if (this.appContext.app.sessionId !== (prevAppContext === null || prevAppContext === void 0 ? void 0 : prevAppContext.app.sessionId)) {
            var sessionDataFields = Session.getFields({
                id: (_f = this.appContext.app.sessionId) !== null && _f !== void 0 ? _f : '',
            });
            // add these to the sink directly to avoid the column being prepended with Data.
            this.oneDsSink.addPersistentDataFields(sessionDataFields);
        }
        // Add language
        if (this.appContext.app.locale !== (prevAppContext === null || prevAppContext === void 0 ? void 0 : prevAppContext.app.locale)) {
            var langDataFields = makeStringDataField('Culture.UiLanguage', (_g = this.appContext.app.locale) !== null && _g !== void 0 ? _g : '');
            // add these to the sink directly to avoid the column being prepended with Data.
            this.oneDsSink.addPersistentDataFields([langDataFields]);
        }
    };
    /**
     * Set the app definition sent as the telemetry metadata
     * @param appDefinition the app definition
     */
    HubSdkTelemetryService.prototype.setAppDefinition = function (appDefinition) {
        this.appDefinition = appDefinition;
    };
    /**
     * Return whether the session id has been set
     */
    HubSdkTelemetryService.prototype.isSessionIdSet = function () {
        var _a;
        return !!((_a = this.appContext) === null || _a === void 0 ? void 0 : _a.app.sessionId);
    };
    /**
     * Create a telemetry event
     * Event names must contain only strings of letters starting with a capital letter, separated by periods.
     * Additionally, event names must be within your tenant's namespace
     * @param scenarioName the scenario name
     */
    HubSdkTelemetryService.prototype.createTelemetryEvent = function (scenarioName, clientTimestamp, appId) {
        var _a, _b, _c, _d, _e, _f, _g;
        return {
            eventName: "".concat(this.namespace, ".").concat(this.sanitizeScenarioName(scenarioName)),
            eventFlags: {
                dataCategories: 2 /* DataCategories.ProductServiceUsage */,
                diagnosticLevel: 110 /* DiagnosticLevel.RequiredServiceData */,
            },
            dataFields: [
                // App metadata. In telemetry App is referred as HostedApp.
                // TODO: Properties to add - HostedApp.Version
                makeStringDataField('HostedAppId', appId !== null && appId !== void 0 ? appId : ''),
                makeStringDataField('HostedAppEntityId', (_b = (_a = this.appContext) === null || _a === void 0 ? void 0 : _a.page.id) !== null && _b !== void 0 ? _b : ''),
                makeStringDataField('HostedAppSdkVersion', (_c = this.appSdkVersion) !== null && _c !== void 0 ? _c : ''),
                makeStringDataField('HostedAppFrameContext', (_e = (_d = this.appContext) === null || _d === void 0 ? void 0 : _d.page.frameContext) !== null && _e !== void 0 ? _e : ''),
                makeStringDataField('HostedAppSessionId', (_g = (_f = this.appContext) === null || _f === void 0 ? void 0 : _f.app.sessionId) !== null && _g !== void 0 ? _g : ''),
            ],
            timestamp: clientTimestamp,
        };
    };
    /**
     * Helper function to sanitize scenario name to work with OTel/Aria
     * This function will remove non-letter characters and
     * capitalize the first character and any character after non-letter character
     * @param name the sanitized name
     */
    HubSdkTelemetryService.prototype.sanitizeScenarioName = function (name) {
        var sanitizedName = '';
        var capitalize = true;
        for (var i = 0; i < name.length; i++) {
            var ch = name.charAt(i);
            if (ch === '.' && i !== 0 && sanitizedName.charAt(sanitizedName.length - 1) !== '.') {
                sanitizedName += ch;
                capitalize = true;
            }
            else if (this.letterRegex.test(ch)) {
                if (capitalize) {
                    capitalize = false;
                    sanitizedName += ch.toUpperCase();
                }
                else {
                    sanitizedName += ch;
                }
            }
            else {
                capitalize = true;
            }
        }
        return sanitizedName;
    };
    HubSdkTelemetryService.prototype.shouldLogToAria = function (name) {
        if (name.startsWith('eventDispatch') || name === 'appInitializationExtended') {
            return false;
        }
        return true;
    };
    return HubSdkTelemetryService;
}());
export { HubSdkTelemetryService };
/**
 * HubSdkTelemetryScenario represents a telemetry scenario logged by the Hub SDK
 */
var HubSdkTelemetryScenario = /** @class */ (function () {
    function HubSdkTelemetryScenario(logger, shouldLogToAria, clientTimestamp, customScenario, activity) {
        this.logger = logger;
        this.shouldLogToAria = shouldLogToAria;
        this.clientTimestamp = clientTimestamp;
        this.customScenario = customScenario;
        this.activity = activity;
        this.lastMark = adjustLastMark(clientTimestamp);
    }
    /**
     * Marks an intermediate point in the overall scenario timeline
     */
    HubSdkTelemetryScenario.prototype.markPhase = function (name) {
        var _a, _b, _c;
        var now = getCurrentMicroseconds();
        var duration = now - this.lastMark;
        this.lastMark = now;
        (_b = (_a = this.activity) === null || _a === void 0 ? void 0 : _a.dataFields) === null || _b === void 0 ? void 0 : _b.push(makeInt64DataField("Activity.Phase.".concat(name), duration));
        (_c = this.customScenario) === null || _c === void 0 ? void 0 : _c.markPhase(name);
    };
    /**
     * Marks the completion of this particular scenario
     */
    HubSdkTelemetryScenario.prototype.stop = function () {
        var _a;
        this.endActivity(true);
        (_a = this.customScenario) === null || _a === void 0 ? void 0 : _a.stop();
    };
    /**
     * Marks a failure of this scenario to complete
     * @param error error information
     */
    HubSdkTelemetryScenario.prototype.fail = function (error) {
        var _a, _b, _c;
        (_b = (_a = this.activity) === null || _a === void 0 ? void 0 : _a.dataFields) === null || _b === void 0 ? void 0 : _b.push(makeStringDataField('ErrorMessage', sanitizeMessage(error)));
        this.endActivity(false);
        (_c = this.customScenario) === null || _c === void 0 ? void 0 : _c.fail(error);
    };
    /**
     * Helper function to end activity
     * @param success whether the activity completed successfully
     */
    HubSdkTelemetryScenario.prototype.endActivity = function (success) {
        // session id must be set first for COGS saving
        if (this.shouldLogToAria && this.activity && this.logger.isSessionIdSet()) {
            this.activity.success = success;
            this.activity.endNow();
        }
    };
    return HubSdkTelemetryScenario;
}());
var getOTelSinkEndpoint = function (dataResidency) {
    switch (dataResidency) {
        case DataResidency.EUDB:
            return OneDSEndpoint.EUDB;
        case DataResidency.DOD:
            return OneDSEndpoint.USGOV_DOD;
        case DataResidency.DOJ:
            return OneDSEndpoint.USGOV_DOJ;
        case DataResidency.Public:
            return OneDSEndpoint.PUBLIC;
        default:
            return undefined;
    }
};
//# sourceMappingURL=telemetry-service.js.map